import React, { useState, useEffect } from 'react';
import { Trash2, RotateCcw, AlertTriangle, X, ShieldAlert, CheckSquare, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export default function TrashBin({ isOpen, onClose, entities = [], title = "Keranjang Sampah", customFilter = null }) {
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());

  // Fetch trash items for all provided entities
  const { data: rawTrashItems = [], isLoading, refetch } = useQuery({
    queryKey: ['trash-bin', ...entities.map(e => e.key)],
    queryFn: async () => {
      let allItems = [];
      for (const entity of entities) {
        const items = await base44.entities[entity.key].listTrash();
        // Add entity metadata to each item
        const mapped = items.map(item => ({
          ...item,
          _entityKey: entity.key,
          _entityLabel: entity.label,
          _displayTitle: entity.getDisplayTitle ? entity.getDisplayTitle(item) : (item.id || 'Tanpa Judul'),
        }));
        allItems = [...allItems, ...mapped];
      }
      return allItems.sort((a, b) => new Date(b.deleted_at) - new Date(a.deleted_at));
    },
    enabled: isOpen && entities.length > 0,
  });

  // Apply custom filter (e.g. for client specific view)
  const trashItems = customFilter ? rawTrashItems.filter(customFilter) : rawTrashItems;

  // Clear selection when closed or items change significantly
  useEffect(() => {
    if (!isOpen) setSelectedItems(new Set());
  }, [isOpen]);

  // Auto-cleanup: remove items older than 14 days
  useEffect(() => {
    if (!isOpen || trashItems.length === 0) return;
    
    const now = new Date().getTime();
    const FOURTEEN_DAYS = 14 * 24 * 60 * 60 * 1000;
    
    const expiredItems = trashItems.filter(item => {
      const deletedAt = new Date(item.deleted_at).getTime();
      return now - deletedAt > FOURTEEN_DAYS;
    });

    if (expiredItems.length > 0) {
      console.log(`[Trash] Auto-cleaning ${expiredItems.length} expired items.`);
      Promise.all(expiredItems.map(item => base44.entities[item._entityKey].hardDelete(item.id)))
        .then(() => refetch());
    }
  }, [trashItems, isOpen, refetch]);

  const handleToggleSelect = (compositeId) => {
    const next = new Set(selectedItems);
    if (next.has(compositeId)) next.delete(compositeId);
    else next.add(compositeId);
    setSelectedItems(next);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === trashItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(trashItems.map(item => `${item._entityKey}-${item.id}`)));
    }
  };

  const handleRestore = async (item) => {
    try {
      // Check for ID collision with active items before restoring
      const activeItems = await base44.entities[item._entityKey].list();
      const conflict = activeItems.find(active => active.id === item.id);
      
      if (conflict) {
        toast.error(
          `⚠️ Tidak bisa dipulihkan! ID "${item.id}" sudah dipakai oleh data aktif. Silakan ganti nama salah satu terlebih dahulu.`,
          { duration: 6000 }
        );
        return;
      }

      await base44.entities[item._entityKey].restore(item.id);
      toast.success(`${item._entityLabel} berhasil dipulihkan!`);
      setSelectedItems(prev => {
        const next = new Set(prev);
        next.delete(`${item._entityKey}-${item.id}`);
        return next;
      });
      refetch();
      queryClient.invalidateQueries({ queryKey: [item._entityKey] });
      queryClient.invalidateQueries();
    } catch (err) {
      toast.error('Gagal memulihkan data.');
    }
  };

  const handleHardDelete = async (item) => {
    if (confirm('Data ini akan dihapus secara permanen dan tidak dapat dikembalikan. Yakin?')) {
      try {
        await base44.entities[item._entityKey].hardDelete(item.id);
        toast.success(`${item._entityLabel} berhasil dihapus permanen.`);
        setSelectedItems(prev => {
          const next = new Set(prev);
          next.delete(`${item._entityKey}-${item.id}`);
          return next;
        });
        refetch();
      } catch (err) {
        toast.error('Gagal menghapus permanen.');
      }
    }
  };

  const handleBatchRestore = async () => {
    const itemsToRestore = trashItems.filter(item => selectedItems.has(`${item._entityKey}-${item.id}`));
    if (itemsToRestore.length === 0) return;

    setIsProcessing(true);
    try {
      // Group by entity key to check conflicts per entity
      const entityKeys = [...new Set(itemsToRestore.map(i => i._entityKey))];
      const activeMap = {};
      for (const key of entityKeys) {
        activeMap[key] = await base44.entities[key].list();
      }

      const safeItems = [];
      const conflictItems = [];
      for (const item of itemsToRestore) {
        const conflict = (activeMap[item._entityKey] || []).find(a => a.id === item.id);
        if (conflict) conflictItems.push(item);
        else safeItems.push(item);
      }

      if (safeItems.length > 0) {
        await Promise.all(safeItems.map(item => base44.entities[item._entityKey].restore(item.id)));
        toast.success(`${safeItems.length} data berhasil dipulihkan!`);
      }
      if (conflictItems.length > 0) {
        toast.error(`⚠️ ${conflictItems.length} data tidak bisa dipulihkan karena ID-nya sudah dipakai. Ganti nama data aktifnya terlebih dahulu.`, { duration: 6000 });
      }

      setSelectedItems(new Set());
      refetch();
      queryClient.invalidateQueries();
    } catch (err) {
      toast.error('Gagal memulihkan beberapa data.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBatchDelete = async () => {
    const itemsToDelete = trashItems.filter(item => selectedItems.has(`${item._entityKey}-${item.id}`));
    if (itemsToDelete.length === 0) return;

    if (confirm(`Hapus permanen ${itemsToDelete.length} data yang dipilih?`)) {
      setIsProcessing(true);
      try {
        await Promise.all(itemsToDelete.map(item => base44.entities[item._entityKey].hardDelete(item.id)));
        toast.success(`${itemsToDelete.length} data berhasil dihapus permanen.`);
        setSelectedItems(new Set());
        refetch();
      } catch (err) {
        toast.error('Gagal menghapus permanen beberapa data.');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleEmptyTrash = async () => {
    if (confirm('Kosongkan semua data di Keranjang Sampah secara permanen?')) {
      setIsProcessing(true);
      try {
        await Promise.all(trashItems.map(item => base44.entities[item._entityKey].hardDelete(item.id)));
        toast.success('Keranjang sampah berhasil dikosongkan.');
        setSelectedItems(new Set());
        refetch();
      } catch (err) {
        toast.error('Gagal mengosongkan keranjang sampah.');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleRestoreAll = async () => {
    if (confirm('Pulihkan SEMUA data dari Keranjang Sampah?')) {
      setIsProcessing(true);
      try {
        await Promise.all(trashItems.map(item => base44.entities[item._entityKey].restore(item.id)));
        toast.success('Semua data berhasil dipulihkan!');
        setSelectedItems(new Set());
        refetch();
        queryClient.invalidateQueries();
      } catch (err) {
        toast.error('Gagal memulihkan semua data.');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const calculateDaysLeft = (deletedAtStr) => {
    const deleted = new Date(deletedAtStr).getTime();
    const now = new Date().getTime();
    const diffDays = Math.ceil((14 * 24 * 60 * 60 * 1000 - (now - deleted)) / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/40  z-50 transition-opacity" onClick={onClose} />
      
      <div className="fixed inset-y-0 right-0 w-full md:w-[500px] bg-white shadow-2xl z-50 flex flex-col transform transition-transform animate-in slide-in-from-right duration-300">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
              <Trash2 className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-800 tracking-tight">{title}</h2>
              <p className="text-[11px] font-semibold text-slate-500">Otomatis terhapus setelah 14 hari</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-500 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/30">
          {isLoading ? (
            <div className="flex justify-center py-20 text-slate-400 font-medium">Memuat data sampah...</div>
          ) : trashItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-slate-400 text-center">
              <ShieldAlert className="w-16 h-16 mb-4 text-slate-200" />
              <p className="text-sm font-bold text-slate-500">Keranjang Sampah Kosong</p>
              <p className="text-xs mt-1">Tidak ada data yang baru saja dihapus.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Select All Bar */}
              <div className="flex items-center justify-between px-2 pb-1">
                <button 
                  onClick={handleSelectAll}
                  className="flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900 transition-colors"
                >
                  {selectedItems.size === trashItems.length ? (
                    <CheckSquare className="w-4 h-4 text-[#D4AF37]" />
                  ) : (
                    <Square className="w-4 h-4 text-slate-400" />
                  )}
                  Pilih Semua ({trashItems.length})
                </button>
                {selectedItems.size > 0 && (
                  <span className="text-xs font-bold text-[#D4AF37] bg-[#F9F5EC] px-2 py-0.5 rounded-md border border-[#E6D5B8]">
                    {selectedItems.size} terpilih
                  </span>
                )}
              </div>

              {/* Items List */}
              {trashItems.map((item) => {
                const compositeId = `${item._entityKey}-${item.id}`;
                const isSelected = selectedItems.has(compositeId);
                
                return (
                  <div 
                    key={compositeId} 
                    className={`bg-white border p-4 rounded-2xl transition-all relative group flex gap-3 ${isSelected ? 'border-[#D4AF37] shadow-sm ring-1 ring-[#D4AF37]/20' : 'border-slate-100 hover:shadow-md'}`}
                  >
                    {/* Checkbox Area */}
                    <div className="pt-1 cursor-pointer" onClick={() => handleToggleSelect(compositeId)}>
                      {isSelected ? (
                        <CheckSquare className="w-5 h-5 text-[#D4AF37]" />
                      ) : (
                        <Square className="w-5 h-5 text-slate-300 group-hover:text-slate-400" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                          {item._entityLabel}
                        </span>
                        <span className={`text-[10px] font-bold ${calculateDaysLeft(item.deleted_at) <= 3 ? 'text-rose-600' : 'text-slate-400'}`}>
                          Sisa {calculateDaysLeft(item.deleted_at)} Hari
                        </span>
                      </div>
                      
                      <h4 className="text-sm font-bold text-slate-800 line-clamp-2 leading-tight mb-1 cursor-pointer" onClick={() => handleToggleSelect(compositeId)}>
                        {item._displayTitle}
                      </h4>
                      <div className="mb-4">
                        <p className="text-[10px] text-slate-400 font-mono">
                          Dihapus: {new Date(item.deleted_at).toLocaleString('id-ID')}
                        </p>
                        {item.deleted_by && (
                          <p className="text-[10px] text-slate-500 font-semibold mt-0.5">
                            Oleh: <span className="font-bold text-[#8C6208]">{item.deleted_by}</span>
                          </p>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button 
                          onClick={() => handleRestore(item)}
                          disabled={isProcessing}
                          className="flex-1 h-8 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-bold rounded-lg gap-1.5 shadow-none"
                        >
                          <RotateCcw className="w-3 h-3" /> Pulihkan
                        </Button>
                        <Button 
                          onClick={() => handleHardDelete(item)}
                          disabled={isProcessing}
                          variant="outline"
                          className="h-8 w-8 p-0 text-slate-400 hover:text-rose-600 hover:bg-rose-50 border-slate-200 rounded-lg shrink-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {trashItems.length > 0 && (
          <div className="p-4 bg-white border-t border-slate-100 flex flex-col gap-2 shadow-[0_-10px_30px_rgba(0,0,0,0.05)]">
            {selectedItems.size > 0 ? (
              // Batch Actions
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  onClick={handleBatchRestore} 
                  disabled={isProcessing}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold gap-2 rounded-xl h-10"
                >
                  <RotateCcw className="w-4 h-4" /> Pulihkan ({selectedItems.size})
                </Button>
                <Button 
                  onClick={handleBatchDelete} 
                  disabled={isProcessing}
                  variant="outline" 
                  className="w-full text-rose-600 border-rose-200 hover:bg-rose-50 font-bold gap-2 rounded-xl h-10"
                >
                  <Trash2 className="w-4 h-4" /> Hapus ({selectedItems.size})
                </Button>
              </div>
            ) : (
              // Global Actions
              <div className="grid grid-cols-2 gap-2">
                <Button 
                  onClick={handleRestoreAll} 
                  disabled={isProcessing}
                  className="w-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-bold gap-2 rounded-xl h-10 shadow-none border border-emerald-200"
                >
                  <RotateCcw className="w-4 h-4" /> Pulihkan Semua
                </Button>
                <Button 
                  onClick={handleEmptyTrash} 
                  disabled={isProcessing}
                  variant="outline" 
                  className="w-full text-rose-600 border-rose-200 hover:bg-rose-50 font-bold gap-2 rounded-xl h-10"
                >
                  <AlertTriangle className="w-4 h-4" /> Kosongkan Semua
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
